import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { X, Minus, Plus } from "lucide-react";
import SmartVariantSelector from "@/modules/storefront/components/SmartVariantSelector";

export default function VariantModal({
  show, onClose, product, selectedVariant, setSelectedVariant,
  quantity, setQuantity, adding, onAddToCart,
}) {
  const variants = product?.variants || [];

  // Quick-view defers to SmartVariantSelector when the server has enriched
  // the product with attribute_groups (new enterprise registry). Otherwise
  // fall back to a minimal "pick a variant" list so old products still work.
  const hasRegistryUI = Array.isArray(product?.attribute_groups) && product.attribute_groups.length > 0;

  if (!show || !product) return null;

  const price = selectedVariant?.price_inr || 0;
  const mrp = selectedVariant?.mrp_inr || price;
  const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const inv = selectedVariant?.warehouse_inventory;
  const available = inv && typeof inv.available === 'number' ? inv.available : null;
  const outOfStock = available !== null && available <= 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="variant-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="variant-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
          <div className="min-w-0 pr-3">
            <h2 className="text-lg font-bold text-gray-900">Select Variant</h2>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{product.title}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 flex-shrink-0" data-testid="variant-modal-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto space-y-5 flex-1 min-h-0">
          {hasRegistryUI ? (
            // Use the same enterprise selector that powers the PDP. Keep
            // parity of behaviour (fuzzy normalization, dependency filter,
            // disabled states for OOS/UNAVAILABLE) between PDP and
            // quick-view so the customer doesn't learn two UIs.
            <SmartVariantSelector
              product={product}
              initialSelection={selectedVariant?.attributes}
              onVariantChange={(v) => { if (v) setSelectedVariant(v); }}
            />
          ) : variants.length > 0 ? (
            // Legacy fallback for products without registered attributes:
            // just list variants so the flow never breaks.
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Choose Variant</p>
              <div className="space-y-2">
                {variants.map(v => {
                  const vInv = v.warehouse_inventory;
                  const vAvail = vInv && typeof vInv.available === 'number' ? vInv.available : null;
                  const vOOS = vAvail !== null && vAvail <= 0;
                  const vLow = vAvail !== null && vAvail > 0 && vAvail <= 5;
                  return (
                    <button key={v.id} onClick={() => !vOOS && setSelectedVariant(v)}
                      data-testid={`variant-option-${v.id}`}
                      disabled={vOOS}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-left ${
                        vOOS
                          ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                          : selectedVariant?.id === v.id
                            ? 'border-[#FFB800] bg-[#FFB800]/10'
                            : 'border-gray-200 hover:border-[#FFB800]'
                      }`}>
                      <div>
                        <p className={`text-sm font-medium ${vOOS ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{v.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{v.sku}</p>
                        {vOOS && <span className="text-[10px] font-semibold text-red-500">Out of Stock</span>}
                        {vLow && <span className="text-[10px] font-semibold text-amber-600">Few left</span>}
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-bold ${vOOS ? 'text-gray-400' : 'text-gray-900'}`}>{'\u20B9'}{v.price_inr?.toLocaleString()}</p>
                        {v.mrp_inr > v.price_inr && (
                          <p className="text-xs text-gray-400 line-through">{'\u20B9'}{v.mrp_inr?.toLocaleString()}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Selected variant summary */}
          {selectedVariant && (
            <div className="bg-gray-50 rounded-xl p-4" data-testid="variant-modal-summary">
              <p className="text-xs text-gray-500 mb-1">{selectedVariant.title} · {selectedVariant.sku}</p>
              <div className="flex items-baseline gap-2 mb-1">
                {mrp > price && (
                  <>
                    <span className="text-lg text-gray-400 line-through">{'\u20B9'}{mrp.toLocaleString()}</span>
                    <span className="text-xs font-semibold text-emerald-600" data-testid="discount-percent-variant">{discount}% off</span>
                  </>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900">{'\u20B9'}{price.toLocaleString()}</span>
                <span className="text-sm text-gray-500">(Inc. of all taxes)</span>
              </div>
              {available !== null && (
                <p className={`text-xs mt-2 font-medium ${available > 5 ? 'text-green-600' : available > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                  {available > 5 ? 'In Stock' : available > 0 ? 'Few left — order soon' : 'Out of Stock'}
                </p>
              )}
            </div>
          )}

          {/* Quantity */}
          {selectedVariant && !outOfStock && (
            <div className="flex items-center justify-between" data-testid="variant-modal-qty-section">
              <span className="text-sm font-semibold text-gray-700">Quantity</span>
              <div className="flex items-center gap-3">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="variant-modal-qty-minus"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-semibold" data-testid="variant-modal-qty-value">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(quantity + 1, available !== null ? Math.max(1, available) : 99))}
                  data-testid="variant-modal-qty-plus"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                  disabled={available !== null && available <= quantity}>
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 space-y-2 flex-shrink-0">
          {!selectedVariant && (
            <p className="text-center text-sm text-amber-600 font-medium pb-1">Please select a variant above</p>
          )}
          <div className="flex gap-2">
            <Button
              className="flex-1 py-5 rounded-xl font-black text-base tracking-wide"
              style={{ background: "#FFB800", color: "#000000" }}
              onClick={onAddToCart}
              disabled={adding || !selectedVariant || outOfStock}
              data-testid="modal-add-to-cart-btn">
              {adding ? 'ADDING...' : outOfStock ? 'OUT OF STOCK' : 'ADD TO CART'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
